/* stylelint-disable declaration-block-no-redundant-longhand-properties */
import { styled, css } from "styled-components";
import theme from "styled-theming";

import Button from "components/Button";
import GoogleMap from "components/GoogleMap";
import { styledLink } from "components/Link/Link.styled";
import TextField from "components/TextField";
import { Container as GridContainer } from "style/components/Page";
import {
    styleBodyS,
    styleBodyM,
    styleBodyL,
    styleHeadingXL,
    styleHeadingXXL,
} from "style/components/Typography";
import { columnGap } from "style/grid";
import { MQ } from "style/mediaQueries";

const buttonColorDisabled = theme("theme", {
    lightgray: "--text-on-neutral-disabled",
    blue: "--text-on-blue-disabled",
    cyan: "--text-on-cyan-disabled",
});

const buttonColor = theme("theme", {
    lightgray: "--text-on-neutral-primary",
    blue: "--text-on-blue-primary",
    cyan: "--text-on-cyan-primary",
});

const errorColor = theme("theme", {
    lightgray: "--label-on-neutral-error",
    blue: "--label-on-blue-error",
    cyan: "--label-on-cyan-error",
});

export const Container = styled(GridContainer)`
    ${columnGap};
    align-items: stretch;
    display: grid;
    grid-template-columns: repeat(4, [col-start] 1fr) [col-end];
    padding-bottom: 0;
    padding-top: 7.875rem;

    ${MQ.FROM_M} {
        grid-template-columns: repeat(12, [col-start] 1fr) [col-end];
        min-height: 100vh;
        padding-top: 10.5rem;
    }

    ${MQ.FROM_XL} {
        padding-top: 14.4375rem;
    }
`;

export const ContentWrapper = styled.div`
    ${columnGap};
    align-self: flex-start;
    display: grid;
    grid-column: col-start 1 / span 4;
    grid-template-columns: repeat(4, [col-start] 1fr) [col-end];
    grid-template-rows: auto auto 1fr;

    ${MQ.FROM_M} {
        grid-column: col-start 1 / span 12;
        grid-template-columns: repeat(12, [col-start] 1fr) [col-end];
    }

    ${MQ.FROM_L} {
        grid-column: col-start 1 / span 5;
        grid-template-columns: repeat(5, [col-start] 1fr) [col-end];
        min-height: calc(100vh - 10.5rem);
    }

    ${MQ.FROM_XL} {
        min-height: calc(100vh - 14.4375rem);
    }
`;

export const Header = styled.h1.attrs<{
    $headingSize: string;
}>(() => ({}))`
    ${(props) =>
        props.$headingSize === "XXL"
            ? css`
                  ${styleHeadingXXL}
              `
            : css`
                  ${styleHeadingXL}
              `};
    grid-column: col-start / col-end;
    margin: 0;
    margin-bottom: 0.875rem;
`;

export const FilterButtons = styled.div`
    display: flex;
    flex-flow: row wrap;
    gap: 0.875rem;
    grid-column: col-start / col-end;
    justify-content: flex-start;

    ${MQ.FROM_M} {
        margin-bottom: 0.875rem;
    }

    ${MQ.FROM_XL} {
        margin-bottom: 1.75rem;
    }
`;

export const StyledForm = styled.form`
    ${columnGap};
    grid-column: col-start / col-end;
    margin-bottom: 2.1875rem;
    margin-top: 2.1875rem;
    position: relative;

    ${MQ.FROM_M} {
        margin-bottom: 1.75rem;
        margin-top: 1.75rem;
    }
`;

export const SearchButton = styled(Button)`
    color: var(${buttonColor});

    &:hover {
        color: var(${buttonColor});
    }

    &.disabled {
        color: var(${buttonColorDisabled});
    }
`;

export const SearchField = styled(TextField)`
    margin-bottom: 1.3125rem;

    ${MQ.FROM_L} {
        margin-bottom: 1.75rem;
    }
`;

export const GpsWrapper = styled.div`
    grid-column: col-start / col-end;
    ${styleBodyM};
`;

export const GpsButton = styled.button.attrs<{
    $hoverSpan?: boolean;
}>(() => ({}))`
    all: unset;
    ${styledLink};
    grid-column: col-start / col-end;
    justify-self: flex-start;

    svg {
        margin-right: 0.875rem;
    }
`;

export const GpsError = styled.div`
    ${styleBodyS}
    color: var(${errorColor});
    grid-column: col-start 1 / span 3;
    margin: 0;
    margin-bottom: 0;
    margin-top: 0.875rem;

    ${MQ.FROM_M} {
        grid-column: col-start 1 / span 4;
    }

    ${MQ.FROM_L} {
        grid-column: col-start 1 / span 3;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 1 / span 2;
    }
`;

export const Description = styled.div`
    ${styleBodyS}
    align-self: flex-end;
    display: none;
    font-style: italic;
    margin: 0;

    ${MQ.FROM_L} {
        display: block;
        grid-column: col-start 1 / span 3;
        margin-bottom: 2.1875rem;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 1 / span 3;
        margin-bottom: 3.0625rem;
    }
`;

export const ResultWrapper = styled.div`
    grid-column: col-start / col-end;

    ${MQ.FROM_L} {
        ${columnGap};
        display: grid;
        grid-column: col-start 6 / col-end;
        grid-template-columns: repeat(6, [col-start] 1fr) [col-end];
        grid-template-rows: auto 1fr;
    }
`;

export const ResultButtons = styled.div`
    align-self: flex-start;
    display: flex;
    flex-direction: row;
    gap: 0.875rem;
    grid-column: col-start / col-end;
    justify-content: flex-end;
    margin-bottom: 1.75rem;

    ${MQ.FROM_XL} {
        margin-bottom: 4.375rem;
    }
`;

export const ResultList = styled.div`
    display: flex;
    flex-flow: column;
    grid-column: 2 / col-end;
    padding-bottom: 4.375rem;
    row-gap: 2.625rem;

    ${MQ.FROM_M} {
        padding-bottom: 6.125rem;
    }

    ${MQ.FROM_XL} {
        padding-bottom: 8.75rem;
    }
`;

export const ResultMap = styled(GoogleMap)`
    grid-column: col-start / col-end;
    min-height: calc(80vh);

    ${MQ.FROM_L} {
        min-height: auto;
    }
`;

export const NoResults = styled.div`
    ${styleBodyL};
    font-style: italic;
`;
